import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import ActivityChart from '../components/analytics/ActivityChart'
import AnalyticsShell from '../components/analytics/AnalyticsShell'
import ColumnPicker from '../components/analytics/ColumnPicker'
import DataTable, { type DataColumn } from '../components/analytics/DataTable'
import EventDetailDrawer, { type AnalyticsEventRow } from '../components/analytics/EventDetailDrawer'
import EventFilterBar from '../components/analytics/EventFilterBar'
import FacetPanel from '../components/analytics/FacetPanel'
import MetricCard from '../components/analytics/MetricCard'
import SessionTimelineDrawer from '../components/analytics/SessionTimelineDrawer'
import WidgetShell from '../components/analytics/WidgetShell'
import { calcDelta, formatCompactNumber, TIME_RANGE_LABELS, type AnalyticsMode } from '../components/analytics/analyticsTypes'
import LiveEventFeed from '../components/LiveEventFeed'
import HistoricalEventMap from '../components/HistoricalEventMap'
import LiveEventMap from '../components/LiveEventMap'
import Button from '../components/ui/Button'
import { analyticsService, type AnalyticsEvent, type BreakdownItem, type TimeRange } from '../services/analyticsService'
import { appsService } from '../services/appsService'
import type { Page } from '../types/navigation'
import { formatPlatform, formatSdkPlatform } from '../utils/platform'
import {
  FACET_PANEL_DIMENSIONS,
  filtersKey,
  formatEventType,
  hasActiveFilters,
  readExploreColumns,
  readExploreChart,
  readExploreFilters,
  toggleDimensionValue,
  writeExploreStateToUrl,
  type Dimension,
  type ExploreChartSettings,
  type ExploreFilters,
} from '../utils/exploreFilters'

interface AppAnalyticsPageProps {
  appId: string
  section: string
  onBack: () => void
  onNavigate: (page: Page) => void
  onNavigateToSDKDocs: () => void
  onNavigateToSettings: () => void
}

const sectionToMode: Record<string, AnalyticsMode> = {
  'app-detail': 'overview',
  'app-live': 'live',
  'app-breakdowns': 'breakdowns',
  'app-explore': 'explore',
  'app-setup': 'setup',
}

export default function AppAnalyticsPage({
  appId,
  section,
  onBack,
  onNavigate,
  onNavigateToSDKDocs,
  onNavigateToSettings,
}: AppAnalyticsPageProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('24h')
  const [selectedEvent, setSelectedEvent] = useState<AnalyticsEventRow | null>(null)
  const [filters, setFilters] = useState<ExploreFilters>(() => readExploreFilters())
  const [chart, setChart] = useState<ExploreChartSettings>(() => readExploreChart())
  const [timelineSessionId, setTimelineSessionId] = useState<string | null>(null)
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(
    () => readExploreColumns() ?? DEFAULT_EVENT_COLUMN_KEYS,
  )
  const mode = sectionToMode[section] ?? 'overview'

  const appQuery = useQuery({
    queryKey: ['app', appId],
    queryFn: () => appsService.getApp(appId),
  })

  const app = appQuery.data
  const bundleId = app?.bundle_id

  const summary = useQuery({
    queryKey: ['app-summary', appId, timeRange],
    enabled: Boolean(bundleId),
    queryFn: () => analyticsService.getSummary(timeRange, { bundleId }),
  })

  const sessionsSeries = useQuery({
    queryKey: ['app-timeseries', appId, timeRange, 'sessions'],
    enabled: Boolean(bundleId),
    queryFn: () => analyticsService.getTimeSeries(timeRange, 'sessions', { bundleId }),
  })

  const eventsSeries = useQuery({
    queryKey: ['app-timeseries', appId, timeRange, 'events'],
    enabled: Boolean(bundleId),
    queryFn: () => analyticsService.getTimeSeries(timeRange, 'events', { bundleId }),
  })

  const breakdowns = useQuery({
    queryKey: ['app-breakdowns-suite', appId, timeRange],
    enabled: Boolean(bundleId),
    queryFn: async () => {
      const types = ['platform', 'sdk_platform', 'sdk_version', 'app_version', 'build_number', 'country', 'city', 'device_model', 'os_version', 'screen_name', 'screen_resolution', 'locale', 'timezone'] as const
      const rows = await Promise.all(types.map(type => analyticsService.getBreakdown(timeRange, type, { bundleId, limit: 8 })))
      return Object.fromEntries(rows.map(row => [row.type, row.data])) as Record<string, BreakdownItem[]>
    },
  })

  const eventWindow = useMemo(() => {
    const end = new Date()
    const rangeDays: Record<TimeRange, number> = {
      '24h': 1,
      '48h': 2,
      '72h': 3,
      '5d': 5,
      '15d': 15,
      '30d': 30,
      '60d': 60,
      '90d': 90,
    }
    const start = new Date(end.getTime() - rangeDays[timeRange] * 24 * 60 * 60 * 1000)
    return { startTime: start.toISOString(), endTime: end.toISOString() }
  }, [timeRange])

  // Explore-only: nothing else reads these, and facets fan out into roughly a
  // dozen queries per filter change.
  const isExplore = mode === 'explore'
  const filterRequest = useMemo(
    () => ({
      dimensions: filters.dimensions as Record<string, string[]>,
      properties: filters.properties,
      ...(filters.sessionId ? { sessionId: filters.sessionId } : {}),
    }),
    [filters],
  )
  const filterQueryKey = filtersKey(filters)

  const events = useInfiniteQuery({
    queryKey: ['app-events', appId, timeRange, filterQueryKey],
    enabled: Boolean(appId) && isExplore,
    initialPageParam: null as EventPageParam,
    queryFn: ({ pageParam }) =>
      analyticsService.queryEvents({
        appId,
        ...eventWindow,
        ...filterRequest,
        limit: EVENTS_PAGE_SIZE,
        ...(pageParam ? { cursor: pageParam.cursor, cursorId: pageParam.cursorId } : {}),
      }),
    getNextPageParam: (lastPage): EventPageParam =>
      lastPage.nextCursor && lastPage.nextCursorId
        ? { cursor: lastPage.nextCursor, cursorId: lastPage.nextCursorId }
        : null,
  })

  const eventRows = useMemo(
    () => events.data?.pages.flatMap(page => page.events) ?? [],
    [events.data],
  )

  const facets = useQuery({
    queryKey: ['app-event-facets', appId, timeRange, filterQueryKey],
    enabled: Boolean(appId) && isExplore,
    queryFn: () =>
      analyticsService.getFacets({
        appId,
        ...eventWindow,
        ...filterRequest,
        facets: FACET_PANEL_DIMENSIONS,
        limit: 8,
      }),
    // Keep the previous breakdown on screen while a refinement loads, rather
    // than collapsing every card to a skeleton on each click.
    placeholderData: previous => previous,
  })

  const formatDimensionValue = useCallback((dimension: Dimension, value: string) => {
    if (dimension === 'platform') return formatPlatform(value)
    if (dimension === 'sdk_platform') return formatSdkPlatform(value)
    if (dimension === 'event_type') return formatEventType(value)
    return value
  }, [])

  const toggleFacet = useCallback((dimension: Dimension, value: string) => {
    setFilters(current => toggleDimensionValue(current, dimension, value))
  }, [])

  // Keep filter and column state in the URL so a view can be shared or reloaded.
  // Only the Explore tab owns these params; App.tsx drops them on other tabs.
  useEffect(() => {
    if (!isExplore) return
    writeExploreStateToUrl(filters, visibleColumnKeys, DEFAULT_EVENT_COLUMN_KEYS, chart)
  }, [isExplore, filters, visibleColumnKeys, chart])

  // Sessions can't be stacked honestly (one session spans several values), so
  // a split only applies to the events metric.
  const effectiveSplit = chart.metric === 'events' ? chart.splitBy : ''
  const series = useQuery({
    queryKey: ['app-event-series', appId, timeRange, filterQueryKey, effectiveSplit],
    enabled: Boolean(appId) && isExplore,
    queryFn: () =>
      analyticsService.getSeries({
        appId,
        ...eventWindow,
        ...filterRequest,
        ...(effectiveSplit ? { splitBy: effectiveSplit } : {}),
      }),
    placeholderData: previous => previous,
  })

  const openSessionTimeline = useCallback((sessionId: string) => {
    setSelectedEvent(null)
    setTimelineSessionId(sessionId)
  }, [])

  const showSessionInTable = useCallback((sessionId: string) => {
    setTimelineSessionId(null)
    setFilters(current => ({ ...current, sessionId }))
  }, [])

  const eventColumns = useMemo(() => buildEventColumns(openSessionTimeline), [openSessionTimeline])

  const visibleEventColumns = useMemo(
    () => eventColumns.filter(column => visibleColumnKeys.includes(column.key)),
    [eventColumns, visibleColumnKeys],
  )

  const tabs: Array<{ id: Page; label: string; mode: AnalyticsMode }> = [
    { id: 'app-detail', label: 'Overview', mode: 'overview' },
    { id: 'app-live', label: 'Live', mode: 'live' },
    { id: 'app-breakdowns', label: 'Breakdowns', mode: 'breakdowns' },
    { id: 'app-explore', label: 'Explore', mode: 'explore' },
    { id: 'app-setup', label: 'Setup', mode: 'setup' },
  ]

  return (
    <AnalyticsShell
      eyebrow={app?.platform ? `${formatPlatform(app.platform)} analytics` : 'App analytics'}
      title={app?.name ?? 'Loading app'}
      description="A focused analytics workspace for product health, live telemetry, dimensions, and event-level investigation."
      timeRange={mode === 'live' ? undefined : timeRange}
      onTimeRangeChange={mode === 'live' ? undefined : setTimeRange}
      actions={
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={onBack}>Apps</Button>
          <Button variant="ghost" size="sm" onClick={onNavigateToSettings}>Settings</Button>
        </div>
      }
    >
      <div className="flex gap-2 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.035] p-1 custom-scrollbar">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => onNavigate(tab.id)}
            className={`mobile-tap-target shrink-0 rounded-xl px-4 text-sm font-semibold ${mode === tab.mode ? 'bg-white text-gray-950' : 'text-gray-400 hover:bg-white/10 hover:text-white'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {mode === 'overview' && (
        <>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <MetricCard label="Sessions" value={summary.data?.summary.uniqueSessions} delta={calcDelta(summary.data?.summary.uniqueSessions ?? 0, summary.data?.priorSummary.uniqueSessions ?? 0)} loading={summary.isLoading} />
            <MetricCard label="Events" value={summary.data?.summary.totalEvents} delta={calcDelta(summary.data?.summary.totalEvents ?? 0, summary.data?.priorSummary.totalEvents ?? 0)} loading={summary.isLoading} />
            <MetricCard label="Crash-Free" value={summary.data ? `${summary.data.summary.crashFreeRate.toFixed(2)}%` : null} delta={calcDelta(summary.data?.summary.crashFreeRate ?? 100, summary.data?.priorSummary.crashFreeRate ?? 100)} loading={summary.isLoading} />
            <MetricCard label="Countries" value={summary.data?.summary.countries.length} loading={summary.isLoading} />
          </div>
          <WidgetShell title="Event Locations" description={`Where activity happened — ${TIME_RANGE_LABELS[timeRange]}.`}>
            <div className="h-[26rem] p-3">
              <HistoricalEventMap timeRange={timeRange} bundleId={bundleId} />
            </div>
          </WidgetShell>
          <div className="grid gap-5 xl:grid-cols-2">
            <SeriesWidget title="Sessions Trend" points={sessionsSeries.data?.data ?? []} loading={sessionsSeries.isLoading} />
            <SeriesWidget title="Events Trend" points={eventsSeries.data?.data ?? []} loading={eventsSeries.isLoading} />
          </div>
        </>
      )}

      {mode === 'live' && (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_26rem]">
          <WidgetShell title="Live Map" description="Event locations as they arrive, in real time.">
            <div className="h-[34rem] p-3">
              <LiveEventMap appId={appId} />
            </div>
          </WidgetShell>
          <div className="h-[38rem]">
            <LiveEventFeed appId={appId} />
          </div>
        </div>
      )}

      {mode === 'breakdowns' && (
        <div className="grid gap-5 xl:grid-cols-2">
          {Object.entries(breakdowns.data ?? {}).map(([type, rows]) => (
            <BreakdownWidget
              key={type}
              title={breakdownTitle(type)}
              rows={rows}
              type={type}
              loading={breakdowns.isLoading}
            />
          ))}
        </div>
      )}

      {mode === 'explore' && (
        <WidgetShell
          title="Event Explorer"
          description="Drill from aggregate signal into raw event samples, properties, session, device, and location."
          action={
            <ColumnPicker
              options={eventColumns.map(column => ({ key: column.key, header: column.header }))}
              visibleKeys={visibleColumnKeys}
              defaultKeys={DEFAULT_EVENT_COLUMN_KEYS}
              onChange={setVisibleColumnKeys}
            />
          }
        >
          <EventFilterBar
            filters={filters}
            onChange={setFilters}
            propertyKeys={facets.data?.propertyKeys.map(key => key.value) ?? []}
            formatValue={formatDimensionValue}
            loadedCount={eventRows.length}
            totalCount={facets.isPlaceholderData ? undefined : facets.data?.total.events}
            hasMore={Boolean(events.hasNextPage)}
            isFetching={events.isLoading}
          />
          <ActivityChart
            data={series.data}
            isLoading={series.isLoading}
            isRefetching={series.isPlaceholderData}
            isError={series.isError}
            metric={chart.metric}
            onMetricChange={metric => setChart(current => ({ ...current, metric }))}
            splitBy={chart.splitBy}
            onSplitByChange={splitBy => setChart(current => ({ ...current, splitBy }))}
            formatValue={formatDimensionValue}
          />
          <FacetPanel
            data={facets.data}
            isLoading={facets.isLoading}
            isError={facets.isError}
            dimensions={FACET_PANEL_DIMENSIONS}
            selected={filters.dimensions}
            onToggle={toggleFacet}
            formatValue={formatDimensionValue}
          />
          <DataTable
            rows={eventRows}
            columns={visibleEventColumns}
            getRowKey={(row) => row.id}
            onRowClick={(row) => setSelectedEvent(row)}
            emptyTitle={hasActiveFilters(filters) ? 'No events match these filters' : 'No matching events'}
            emptyDescription={
              hasActiveFilters(filters)
                ? 'Clear or widen the filters, or try a longer time range.'
                : 'Try a broader time range or wait for new telemetry.'
            }
          />
          {events.hasNextPage && (
            <div className="border-t border-white/10 p-4 text-center">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => events.fetchNextPage()}
                disabled={events.isFetchingNextPage}
              >
                {events.isFetchingNextPage ? 'Loading…' : `Load ${EVENTS_PAGE_SIZE} more`}
              </Button>
            </div>
          )}
        </WidgetShell>
      )}

      {mode === 'setup' && (
        <WidgetShell title="Setup And Instrumentation" description="Keep operational setup separate from analytics investigation.">
          <div className="grid gap-4 p-5 md:grid-cols-3">
            <SetupCard title="API Keys" description="Create and rotate keys from Settings." action="Open settings" onClick={onNavigateToSettings} />
            <SetupCard title="SDK Docs" description="Review installation and instrumentation guidance." action="Open docs" onClick={onNavigateToSDKDocs} />
            <SetupCard title="First Event" description="Ship, launch, and watch the Live tab for telemetry." action="View live" onClick={() => onNavigate('app-live')} />
          </div>
        </WidgetShell>
      )}

      <EventDetailDrawer
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onOpenSession={openSessionTimeline}
      />
      <SessionTimelineDrawer
        sessionId={timelineSessionId}
        appId={appId}
        onClose={() => setTimelineSessionId(null)}
        onShowInTable={showSessionInTable}
      />
    </AnalyticsShell>
  )
}

function SeriesWidget({ title, points, loading }: { title: string; points: Array<{ timestamp: string; date?: string; value: number }>; loading: boolean }) {
  const max = Math.max(...points.map(point => point.value), 1)
  return (
    <WidgetShell title={title} description="Range-aware trend using the analytics time-series endpoint.">
      <div className="p-5">
        {loading ? (
          <div className="h-44 stat-skeleton" />
        ) : (
          <div className="flex h-44 items-end gap-1">
            {points.map((point, index) => (
              <div key={`${point.timestamp}-${index}`} className="flex flex-1 items-end">
                <div className="w-full rounded-t bg-gradient-to-t from-indigo-500/50 to-cyan-300" style={{ height: `${Math.max(2, (point.value / max) * 100)}%` }} title={`${point.value}`} />
              </div>
            ))}
          </div>
        )}
      </div>
    </WidgetShell>
  )
}

function BreakdownWidget({ title, rows, type, loading }: { title: string; rows: BreakdownItem[]; type: string; loading: boolean }) {
  const max = Math.max(...rows.map(row => row.events), 1)
  return (
    <WidgetShell title={title} description="Dimension breakdown with drilldown-ready table semantics.">
      <div className="space-y-2 p-4">
        {loading ? <div className="h-40 stat-skeleton" /> : rows.map(row => (
          <div key={row.label} className="rounded-2xl border border-white/10 bg-white/[0.035] p-3">
            <div className="flex items-center justify-between gap-3">
              <span className="truncate font-medium text-white">{formatBreakdownLabel(type, row.label)}</span>
              <span className="text-sm tabular-nums text-gray-400">{formatCompactNumber(row.events)} events</span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/5">
              <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-300" style={{ width: `${Math.max(4, (row.events / max) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </WidgetShell>
  )
}

function SetupCard({ title, description, action, onClick }: { title: string; description: string; action: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="rounded-3xl border border-white/10 bg-white/[0.035] p-5 text-left hover:bg-white/[0.07]">
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-gray-400">{description}</p>
      <p className="mt-5 text-sm font-semibold text-indigo-300">{action}</p>
    </button>
  )
}

const EVENTS_PAGE_SIZE = 100

type EventPageParam = { cursor: string; cursorId: string } | null

/** Columns shown when the URL does not pin a set. Matches the original table. */
const DEFAULT_EVENT_COLUMN_KEYS = ['time', 'event', 'screen', 'session', 'device', 'sdk', 'build', 'location']

function summariseProperties(properties: Record<string, unknown> | null | undefined): string {
  if (!properties) return '—'
  const keys = Object.keys(properties)
  if (keys.length === 0) return '—'
  const preview = keys.slice(0, 3).join(', ')
  return keys.length > 3 ? `${preview} +${keys.length - 3}` : preview
}

/**
 * Built per-render rather than defined as a constant so the session cell can
 * open that session's timeline -- the fastest path from "this event looks
 * wrong" to "show me everything around it".
 */
function buildEventColumns(onOpenSession: (sessionId: string) => void): DataColumn<AnalyticsEvent>[] {
  return [
    { key: 'time', header: 'Time', render: row => new Date(row.timestamp).toLocaleString() },
    { key: 'event', header: 'Event', render: row => <span className="font-semibold text-white">{row.event_name || row.event_type}</span> },
    { key: 'event_type', header: 'Type', render: row => <span className="font-mono text-xs text-gray-400">{row.event_type}</span> },
    { key: 'screen', header: 'Screen', render: row => row.screen_name || '—' },
    {
      key: 'session',
      header: 'Session',
      render: row =>
        row.session_id ? (
          <button
            type="button"
            title="Open this session's timeline"
            onClick={event => {
              event.stopPropagation()
              onOpenSession(row.session_id as string)
            }}
            className="font-mono text-xs text-gray-300 underline decoration-dotted underline-offset-4 transition-colors hover:text-indigo-300"
          >
            {row.session_id}
          </button>
        ) : (
          '—'
        ),
    },
    { key: 'app', header: 'App', render: row => row.app_name || row.bundle_id || '—' },
    { key: 'device', header: 'Device', render: row => [formatPlatform(row.platform), row.device_model].filter(Boolean).join(' · ') || '—' },
    { key: 'os', header: 'OS', render: row => row.os_version || '—' },
    { key: 'sdk', header: 'SDK', render: row => [formatSdkPlatform(row.sdk_platform), row.sdk_version].filter(value => value && value !== 'Unknown SDK').join(' · ') || row.sdk_version || '—' },
    { key: 'build', header: 'Build', render: row => [row.app_version, row.build_number].filter(Boolean).join(' · ') || '—' },
    { key: 'location', header: 'Location', render: row => [row.city, row.country].filter(Boolean).join(', ') || '—' },
    { key: 'resolution', header: 'Resolution', render: row => row.screen_resolution || '—' },
    { key: 'locale', header: 'Locale', render: row => row.locale || '—' },
    { key: 'timezone', header: 'Timezone', render: row => row.timezone || '—' },
    {
      key: 'crash',
      header: 'Crash Reason',
      render: row => (row.crash_reason ? <span className="text-rose-300">{row.crash_reason}</span> : '—'),
    },
    {
      key: 'properties',
      header: 'Properties',
      render: row => <span className="font-mono text-xs text-gray-400">{summariseProperties(row.properties)}</span>,
    },
  ]
}

function breakdownTitle(type: string) {
  const titles: Record<string, string> = {
    platform: 'Platform',
    sdk_platform: 'SDK Producer',
    sdk_version: 'SDK Version',
    app_version: 'App Version',
    build_number: 'Build Number',
    country: 'Country',
    city: 'City',
    device_model: 'Device Model',
    os_version: 'OS Version',
    screen_name: 'Screen',
    screen_resolution: 'Screen Resolution',
    locale: 'Locale',
    timezone: 'Timezone',
  }
  return titles[type] ?? type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatBreakdownLabel(type: string, label: string) {
  if (!label) return 'Unknown'
  if (type === 'platform') return formatPlatform(label)
  if (type === 'sdk_platform') return formatSdkPlatform(label)
  return label
}
